import React, { useEffect, useState } from "react";
import { Box, Stack, Switch, Typography } from "@mui/material";
import PageHeader from "../../components/PageHeader";
import ApiService from "../../services/api-service";
import notifier from "../../components/Notifier";
import { INK, Muted, SectionLabel } from "../../components/Ink";
import { NOTIFICATION_GROUPS, normalizePreferences } from "../../utils/gamification/notificationTypes";

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: INK.accent },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: INK.accent, opacity: 0.5 },
  "& .MuiSwitch-track": { backgroundColor: INK.dim },
};

// `/app/Settings/Notifications` — one switch per v2 notification type.
export default function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    let alive = true;
    ApiService.NOTIFICATION_PREFERENCES_GET()
      .then((res) => alive && setPrefs(normalizePreferences(res?.result ?? res)))
      .catch((error) => {
        console.error("[NotificationSettings] load failed", error?.message);
        if (alive) {
          setFailed(true);
          setPrefs(normalizePreferences({}));
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  const toggle = async (key, value) => {
    const previous = prefs[key];
    setPrefs((p) => ({ ...p, [key]: value }));
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await ApiService.NOTIFICATION_PREFERENCES_PUT({ [key]: value });
    } catch (error) {
      console.error("[NotificationSettings] save failed", key, error?.message);
      setPrefs((p) => ({ ...p, [key]: previous }));
      notifier.error("Could not save that preference");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", px: { xs: 2, sm: 4 }, py: 3, maxWidth: 680, mx: "auto" }}>
      <PageHeader title="Notifications" />
      <Muted sx={{ mt: 2 }}>Everything is on by default. Turn off what you do not want; changes save immediately.</Muted>
      {failed ? <Muted sx={{ mt: 1, color: INK.dim }}>Could not load saved preferences; showing defaults.</Muted> : null}

      {prefs
        ? NOTIFICATION_GROUPS.map((group) => (
            <Box key={group.key}>
              <SectionLabel>{group.title}</SectionLabel>
              {group.types.map((t) => (
                <Stack key={t.key} direction="row" alignItems="center" spacing={2} sx={{ py: 1.25, borderBottom: `1px solid ${INK.rule}` }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography component="label" htmlFor={`pref-${t.key}`} sx={{ display: "block", fontSize: 14, color: INK.text, fontWeight: 500, cursor: "pointer" }}>{t.title}</Typography>
                    <Muted sx={{ fontSize: 12 }}>{t.description}</Muted>
                  </Box>
                  <Switch id={`pref-${t.key}`} size="small" checked={Boolean(prefs[t.key])} disabled={Boolean(saving[t.key])} onChange={(e) => toggle(t.key, e.target.checked)} sx={switchSx} />
                </Stack>
              ))}
            </Box>
          ))
        : <Muted sx={{ mt: 3 }}>Loading…</Muted>}
    </Box>
  );
}
